import {
  Box,
  Button,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { foyerApi, type DeviceLogLine } from "../http/foyer";

interface Props {
  premiseId: string;
  screenId: string;
}

type Level = "problems" | "all";

const PAGE = 200;

/**
 * The TV's continuously shipped log (warn/error plus player and signage
 * lines, kept 14 days), read newest first.
 *
 * "Capture logs" in the Actions list is a snapshot taken now. This panel
 * shows what the box already sent over the past two weeks, so it covers the
 * case where nobody captured anything at the moment a problem happened.
 */
export default function DeviceLogPanel({ premiseId, screenId }: Props) {
  const [level, setLevel] = useState<Level>("problems");
  const [lines, setLines] = useState<DeviceLogLine[]>([]);
  const [nextBefore, setNextBefore] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(
    async (before?: string) => {
      setLoading(true);
      setError(null);
      try {
        const page = await foyerApi.screenLogEntries(premiseId, screenId, {
          level,
          before,
          limit: PAGE,
        });
        setLines((prev) => (before ? [...prev, ...page.lines] : page.lines));
        setNextBefore(page.nextBefore);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    },
    [premiseId, screenId, level]
  );

  useEffect(() => {
    setLines([]);
    setNextBefore(undefined);
    void load();
  }, [load]);

  // Text filter over what is already loaded. Server-side text search would
  // scan an unindexed field across up to hundreds of thousands of rows.
  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return q ? lines.filter((l) => l.message.toLowerCase().includes(q)) : lines;
  }, [lines, filter]);

  return (
    <Box data-testid="device-log-panel">
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 1, flexWrap: "wrap", rowGap: 1 }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={level}
          onChange={(_, v: Level | null) => v && setLevel(v)}
        >
          <ToggleButton value="problems">Warnings & errors</ToggleButton>
          <ToggleButton value="all">Everything shipped</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          size="small"
          placeholder="Filter loaded lines"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ minWidth: 180, flex: 1 }}
        />
        <Button size="small" onClick={() => void load()} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      {error && (
        <Typography
          variant="caption"
          color="error.main"
          sx={{ display: "block" }}
        >
          Could not load logs: {error}
        </Typography>
      )}

      {!error && !loading && lines.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {level === "problems"
            ? "No warnings or errors shipped in the last 14 days."
            : "Nothing shipped in the last 14 days. A TV on an older build does not send logs."}
        </Typography>
      )}

      {shown.length > 0 && (
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1,
            maxHeight: 320,
            overflow: "auto",
            fontSize: 11,
            lineHeight: 1.45,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            bgcolor: "action.hover",
            borderRadius: 1,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {shown.map((l, i) => (
            <Box
              component="span"
              key={`${l.ts}-${i}`}
              sx={{
                display: "block",
                color:
                  l.level === "error"
                    ? "error.main"
                    : l.level === "warn"
                      ? "warning.main"
                      : "text.primary",
              }}
            >
              {new Date(l.ts).toLocaleString()} [{l.level}] {l.message}
            </Box>
          ))}
        </Box>
      )}

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
        {nextBefore && (
          <Button
            size="small"
            onClick={() => void load(nextBefore)}
            disabled={loading}
          >
            Load older
          </Button>
        )}
        {lines.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {filter.trim()
              ? `${shown.length} of ${lines.length} loaded lines`
              : `${lines.length} lines loaded`}
            {" · device clock times"}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
